/**
 * Server reads for the supplier portal.
 *
 * Each board column is its OWN infinite query: one slow or failing column
 * never blocks the others, and each pages independently because a supplier can
 * hold thousands of orders. Board data moves constantly, so REALTIME.
 */
import { useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { CACHE_TIMES } from "@/lib/queryConfig";

import type { BoardWindow } from "@/modules/SupplierOrders";

import { bootstrapCacheEntries, needsBootstrap } from "../lib/bootstrap.ts";
import type { PortalBoardFilters, PortalScope } from "../types.ts";
import { supplierPortalApi } from "./supplierPortal.api.ts";
import { portalKeys, scopeKey } from "./supplierPortalQueryKeys.ts";

/** `enabled` waits for a signed-in user, so a signed-out visit never probes the API. */
export function useSupplierSession(enabled = true) {
  return useQuery({
    queryKey: portalKeys.session,
    queryFn: () => supplierPortalApi.getSession(),
    enabled,
    ...CACHE_TIMES.STANDARD,
    retry: false,
  });
}

/** `enabled` lets the board page wait for `usePortalBootstrapQuery`, which seeds this key. */
export function usePortalBoard(scope: PortalScope, enabled = true) {
  return useQuery({
    queryKey: portalKeys.board(scope),
    queryFn: () => supplierPortalApi.getBoard(scope),
    enabled,
    ...CACHE_TIMES.STANDARD,
  });
}

interface FirstLoad {
  scopeKey: string;
  filters: PortalBoardFilters;
  needed: boolean;
}

function firstLoad(queryClient: QueryClient, scope: PortalScope, filters: PortalBoardFilters): FirstLoad {
  return {
    scopeKey: scopeKey(scope),
    filters,
    needed: needsBootstrap({
      boardCached: queryClient.getQueryData(portalKeys.board(scope)) !== undefined,
      countsCached: queryClient.getQueryData(portalKeys.counts(scope, filters.window)) !== undefined,
    }),
  };
}

/**
 * The board page's first load as ONE request: board, summary, methods, counts
 * and every visible column's first page, written straight into those slices'
 * own cache keys before the board renders. Every slice is then fresh on mount,
 * so none of them fetches; after that, refetches, invalidation after writes,
 * "load more" and filter changes all go through the slice queries as before.
 *
 * FIRST LOAD ONLY: the filters and the cold/warm decision are fixed when the
 * page mounts (and again if the scope changes). A later filter or window
 * change does not re-bootstrap — the slices whose keys changed fetch
 * themselves, which is simpler than re-gating the mounted board behind a
 * skeleton, and is what the board did before.
 *
 * `settled` is false only while a bootstrap is in flight; the page holds the
 * slices back until then. If bootstrap fails it settles too, and the slices
 * fetch themselves exactly as before (their own errors still show).
 */
export function usePortalBootstrapQuery(scope: PortalScope, filters: PortalBoardFilters, pageSize: number) {
  const queryClient = useQueryClient();
  const [first, setFirst] = useState(() => firstLoad(queryClient, scope, filters));
  if (first.scopeKey !== scopeKey(scope)) setFirst(firstLoad(queryClient, scope, filters));

  const query = useQuery({
    queryKey: portalKeys.bootstrap(scope, first.filters, pageSize),
    queryFn: async () => {
      const data = await supplierPortalApi.getBootstrap(scope, { filters: first.filters, limit: pageSize });
      for (const entry of bootstrapCacheEntries(scope, first.filters, pageSize, data)) {
        queryClient.setQueryData(entry.queryKey, entry.data);
      }
      return data;
    },
    enabled: first.needed,
    /*
     * A one-shot loader, not a cache anyone reads: the slices own freshness.
     * It never refetches by itself (that would race the slices' own refetch)
     * and is dropped on unmount, and a failure falls back at once, not after
     * retries.
     */
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { settled: !first.needed || !query.isPending };
}

export function usePortalSummary(scope: PortalScope, window: BoardWindow) {
  return useQuery({
    queryKey: portalKeys.summary(scope, window),
    queryFn: () => supplierPortalApi.getSummary(scope, window),
    ...CACHE_TIMES.REALTIME,
  });
}

export function usePortalMethods(scope: PortalScope, window: BoardWindow) {
  return useQuery({
    queryKey: portalKeys.methods(scope, window),
    queryFn: () => supplierPortalApi.getMethods(scope, window),
    ...CACHE_TIMES.STANDARD,
  });
}

export function useColumnCounts(scope: PortalScope, window: BoardWindow, enabled = true) {
  return useQuery({
    queryKey: portalKeys.counts(scope, window),
    queryFn: () => supplierPortalApi.getColumnCounts(scope, window),
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

export function useColumnOrders(
  scope: PortalScope,
  columnId: string,
  filters: PortalBoardFilters,
  pageSize: number,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: portalKeys.column(scope, columnId, filters, pageSize),
    queryFn: ({ pageParam }) =>
      supplierPortalApi.getColumnPage(scope, { columnId, filters, cursor: pageParam, limit: pageSize }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

export function useScheduleRange(scope: PortalScope, from: string, to: string, filters: PortalBoardFilters) {
  return useQuery({
    queryKey: portalKeys.schedule(scope, from, to, filters),
    queryFn: () => supplierPortalApi.getScheduleRange(scope, { from, to, filters }),
    ...CACHE_TIMES.REALTIME,
  });
}

export function usePortalOrder(scope: PortalScope, supplierOrderId: string | null) {
  return useQuery({
    queryKey: portalKeys.order(scope, supplierOrderId ?? ""),
    queryFn: () => supplierPortalApi.getOrder(scope, supplierOrderId!),
    enabled: Boolean(supplierOrderId),
    ...CACHE_TIMES.REALTIME,
  });
}

export function usePortalComments(scope: PortalScope, supplierOrderId: string, enabled = true) {
  return useQuery({
    queryKey: portalKeys.comments(scope, supplierOrderId),
    queryFn: () => supplierPortalApi.getComments(scope, supplierOrderId),
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

export function usePortalHistory(scope: PortalScope, supplierOrderId: string, enabled = true) {
  return useQuery({
    queryKey: portalKeys.history(scope, supplierOrderId),
    queryFn: () => supplierPortalApi.getHistory(scope, supplierOrderId),
    enabled,
    ...CACHE_TIMES.REALTIME,
  });
}

export function useSupplierCodeSearch(scope: PortalScope, query: string, enabled: boolean) {
  return useQuery({
    queryKey: portalKeys.codes(scope, query.trim().toLowerCase()),
    queryFn: () => supplierPortalApi.searchSupplierCodes(scope, { query }),
    enabled,
    ...CACHE_TIMES.STANDARD,
  });
}
